import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { makePost } from "../../utils/fetchUtils";
import { RESET_PASSWORD_TOKEN } from "../../utils/constants";
import nclude from "../../assets/nclude.svg";
import sticker1 from "../../assets/sticker1.svg";
import curve1 from "../../assets/curve.svg";
import greaterThan from "../../assets/greaterthan.svg";
import useWindowWidth from "../../utils/windowWidth";

const ResetPasswordToken = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const mainRef = useRef<HTMLHeadingElement>(null);

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [successMsg, setSuccessMsg] = useState<string>("");

  const [strength, setStrength] = useState(0);
  const [strengthLabel, setStrengthLabel] = useState("Weak");

  useEffect(() => {
    document.title = "Set New Password - Nclude Portal";
    if (mainRef.current) {
      mainRef.current.focus();
    }
  }, [location]);

  const checkPasswordStrength = (pass: string) => {
    let score = 0;
    if (pass.length >= 8) score += 25;
    if (/[A-Z]/.test(pass)) score += 25;
    if (/[0-9]/.test(pass)) score += 25;
    if (/[@$!%*?&]/.test(pass)) score += 25;
    setStrength(score);
    if (score < 50) setStrengthLabel("Weak");
    else if (score < 75) setStrengthLabel("Medium");
    else setStrengthLabel("Strong");
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccessMsg("");

    if (!token) {
      setError("Invalid or missing password reset token. Please request a new link.");
      return;
    }
    if (!newPassword || newPassword.length < 8) {
      setError("Password must be at least 8 characters long.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      const body = {
        token: token,
        newPassword: newPassword
      };
      const resp = await makePost(RESET_PASSWORD_TOKEN, JSON.stringify(body));
      setLoading(false);

      if (resp && resp.success !== false) {
        setSuccessMsg(resp.message || "Password has been reset successfully. You can now log in with your new password.");
        setTimeout(() => {
          navigate("/login");
        }, 2000);
      } else {
        setError(resp.message || "Invalid or expired password reset link. Please request a new one.");
      }
    } catch (err: any) {
      setLoading(false);
      if (err.response && err.response.data && err.response.data.message) {
        setError(err.response.data.message);
      } else {
        setError("Invalid or expired password reset link. Please request a new one.");
      }
    }
  };

  const windowWidth = useWindowWidth();
  const isMobile = windowWidth < 769;

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  return (
    <div className="auth-page flex items-center login-form" style={{ minHeight: isMobile ? "50vh" : "100vh" }} role="main">
      <div className="logo-container">
        <div className="flex justify-center items-center h-screen w-full">
          <div className="flex flex-col items-start">
            <img src={nclude} alt="Nclude logo" className="logo mb-3" />
            <div className="flex flex-col items-start text-container">
              <img src={sticker1} alt="" aria-hidden="true" className="sticker1 mb-2" />
              <img src={curve1} alt="" aria-hidden="true" className="curve1 mb-4" />
              <h3 className="text-left font-normal">
                Resources <br />
                and opportunities for <span className="font-bold tracking-normal text-white">People with Disabilities</span>
              </h3>
            </div>
          </div>
        </div>
      </div>

      <div className="auth-form-container flex flex-col items-center">
        <div className="auth-form">
          <h1
            className="mt-12 mb-2 text-[2rem]"
            ref={mainRef}
            tabIndex={-1}
            style={{ fontWeight: 600, lineHeight: "2.375rem" }}
          >
            Set New Password
          </h1>
          <h2
            className="mb-4"
            style={{ color: "#787486", fontWeight: 500, fontSize: "1rem" }}
          >
            Please enter your new password below.
          </h2>

          {error && (
            <div className="p-3 my-3 bg-red-50 border border-red-200 rounded-lg text-center">
              <p className="text-[#FF0000] font-medium" role="alert">
                {error}
              </p>
              <div className="mt-2">
                <Link to="/forgot-password" className="text-[#5030E5] text-sm underline font-semibold">
                  Request a new Reset Link
                </Link>
              </div>
            </div>
          )}

          {successMsg && (
            <div className="p-3 my-3 bg-green-50 border border-green-200 rounded-lg text-center">
              <p className="text-[#15803d] font-medium" role="status">
                {successMsg}
              </p>
              <p className="text-xs text-gray-600 mt-1">
                Redirecting to login page...
              </p>
            </div>
          )}

          {!successMsg && (
            <form onSubmit={handleResetPassword} className="flex flex-col">
              <div style={{ position: "relative" }}>
                <label htmlFor="token-new-password">
                  <span className="border-label">New Password</span>
                </label>
                <input
                  className="w-[100%] h-[4.5rem] pl-4 pr-12 mt-4 input input-password"
                  id="token-new-password"
                  type={showPassword ? "text" : "password"}
                  placeholder="8+ characters"
                  value={newPassword}
                  required
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    checkPasswordStrength(e.target.value);
                    setError("");
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  style={{
                    position: "absolute",
                    right: "1rem",
                    top: "2rem",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "#6b7280",
                    padding: "0.25rem",
                    zIndex: 10
                  }}
                >
                  {showPassword ? (
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858-5.908a10.048 10.048 0 014.122-.963c4.478 0 8.268 2.943 9.542 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21M3 3l18 18" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>

              <div className="d-flex align-items-center mt-2" style={{ gap: "10px" }}>
                <div className="progress-bar-container" style={{ flexGrow: 1, background: "#ddd", height: "8px", borderRadius: "4px" }}>
                  <div
                    style={{
                      width: strength >= 75 ? "100%" : `${strength}%`,
                      height: "100%",
                      borderRadius: "4px",
                      background: strength < 50 ? "red" : strength < 75 ? "orange" : "green",
                      transition: "width 0.3s ease-in-out"
                    }}
                  />
                </div>
                <span style={{ fontWeight: "bold", fontSize: "0.85rem", color: strength < 50 ? "red" : strength < 75 ? "orange" : "green" }}>
                  {strengthLabel}
                </span>
              </div>

              <div style={{ position: "relative" }} className="mt-4">
                <label htmlFor="token-confirm-password">
                  <span className="border-label">Confirm Password</span>
                </label>
                <input
                  className="w-[100%] h-[4.5rem] pl-4 pr-12 mt-4 input input-password"
                  id="token-confirm-password"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm 8+ characters"
                  value={confirmPassword}
                  required
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    setError("");
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
                  style={{
                    position: "absolute",
                    right: "1rem",
                    top: "2rem",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "#6b7280",
                    padding: "0.25rem",
                    zIndex: 10
                  }}
                >
                  {showConfirmPassword ? (
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858-5.908a10.048 10.048 0 014.122-.963c4.478 0 8.268 2.943 9.542 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21M3 3l18 18" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>

              <button
                type="submit"
                className="mt-8 bg-[#4f00ec] w-[14rem] h-[3.875rem] rounded submit-button"
                disabled={loading || !token}
              >
                {loading ? "Resetting..." : "Reset Password"}
                <img src={greaterThan} alt="" aria-hidden="true" />
              </button>
            </form>
          )}

          <h2 style={{ fontSize: "1rem", fontWeight: 500 }} className="mt-8 text-right">
            Back to{" "}
            <Link to="/login" className="text-[#5030E5]" style={{ textDecoration: "underline" }}>
              Login
            </Link>
          </h2>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordToken;
